import type { FeatureFlag } from "./types";

/**
 * Sentinel domain for flags that don't follow the dotted
 * `domain.[subgroup.]name` convention (e.g. legacy `ENABLE_*` keys still
 * in flight during the backend rename). Rendered as the "Other" group and
 * always sorted last. Empty string can never collide with a real domain.
 *
 * Prefer {@link isUngroupedDomain} at call sites over comparing to this
 * value directly — keep the sentinel an implementation detail.
 */
export const UNGROUPED_DOMAIN = "";

export interface FeatureFlagGroup {
  /** First dotted segment, or {@link UNGROUPED_DOMAIN} when there's no dot. */
  readonly domain: string;
  readonly flags: readonly FeatureFlag[];
}

function domainOf(id: string): string {
  const dot = id.indexOf(".");
  // A leading dot (dot at index 0) yields an empty first segment, which we
  // treat as ungrouped rather than a real domain.
  return dot > 0 ? id.slice(0, dot) : UNGROUPED_DOMAIN;
}

/** Whether a group is the catch-all bucket for keys without a domain. */
export function isUngroupedDomain(domain: string): boolean {
  return domain === UNGROUPED_DOMAIN;
}

/**
 * Capitalized fallback label for a domain that has no explicit i18n entry
 * (a new or unknown bounded context). Display copy for known domains lives
 * in `featureFlags.group.domains.*`; this is only the last resort.
 */
export function domainLabel(domain: string): string {
  return domain.charAt(0).toUpperCase() + domain.slice(1);
}

/**
 * Groups feature flags by their bounded-context domain (the first dotted
 * segment of the key). Pure and non-mutating with respect to its input:
 * groups are ordered alphabetically by domain with the ungrouped bucket
 * last, and flags inside each group are ordered by id.
 */
export function groupFlagsByDomain(items: readonly FeatureFlag[]): readonly FeatureFlagGroup[] {
  const byDomain = new Map<string, FeatureFlag[]>();

  for (const flag of items) {
    const domain = domainOf(flag.id);
    const bucket = byDomain.get(domain);
    if (bucket) {
      // `bucket` is a locally-owned accumulator array, never the input —
      // push is the idiomatic O(1) insert (vs. an O(n²) spread per flag).
      bucket.push(flag);
    } else {
      byDomain.set(domain, [flag]);
    }
  }

  return [...byDomain.entries()]
    .map(([domain, flags]) => ({
      domain,
      flags: [...flags].sort((a, b) => a.id.localeCompare(b.id)),
    }))
    .sort((a, b) => {
      if (isUngroupedDomain(a.domain)) return 1;
      if (isUngroupedDomain(b.domain)) return -1;
      return a.domain.localeCompare(b.domain);
    });
}
